import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@services/notification.service';
import { CommonConfigFacade } from '@shared/common-config/state/common-config.facade';
import { ConfirmDialogService } from '@shared/confirm-dialog/confirm-dialog.service';
import { ICountry, ILanguage, IProfilePhotoUpdate, ITimezone, IUser } from '@shared/interfaces';
import * as _ from 'lodash';
import { Subscription, throwError } from 'rxjs';
import { ProfileService } from '../profile.service';
import { USER_DASHBOARD_BASE_PATH } from '../routes/profile.routes.names';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent implements OnInit {
  userProfileForm: UntypedFormGroup;
  base64Image: string;
  timeZones: ITimezone[];
  countryList: ICountry[];
  selectedCountryCode;
  languageList: ILanguage[];

  @Input() response: IUser;
  @Output() updatedUser: EventEmitter<IUser> = new EventEmitter();
  userDetail: IUser;
  subscriptions: Subscription[] = [];
  isAnyValueUpdated = false;
  isChangeUserFormValue = false;
  defaultTimeZone = {
    abbreviation: 'GMT',
    id: '69',
    name: 'Greenwich Mean Time (GMT)',
    offset: '+00:00'
  };
  constructor(
    private readonly notificationService: NotificationService,
    private readonly profileService: ProfileService,
    private commonConfigFacade: CommonConfigFacade,
    private formBuilder: UntypedFormBuilder,
    private dateAdapter: DateAdapter<Date>,
    private translate: TranslateService,
    private confirmDialogService: ConfirmDialogService,
    private router: Router
  ) {}

  ngOnInit() {
    try {
      this.userDetail = this.response;
      this.initializeForm();

      // set current selected language in date picker
      this.dateAdapter.setLocale(this.translate.currentLang);
      this.subscribeFormValueChanges();
    } catch (error) {
      this.notificationService.error(error.message);
    }
  }

  private initializeForm() {
    if (this.userDetail) {
      this.userProfileForm = this.formBuilder.group({
        userName: [{ value: this.userDetail.userName, disabled: true }, [Validators.required]],
        firstName: [this.userDetail.firstName, [Validators.required]],
        middleName: [this.userDetail.middleName ? this.userDetail.middleName : ''],
        lastName: [this.userDetail.lastName, [Validators.required]],
        dateOfBirth: [this.userDetail.dateOfBirth],
        phoneNumber: [
          this.userDetail.phoneNumber ? this.userDetail.phoneNumber : '',
          [Validators.max(Number.MAX_VALUE), Validators.min(0)]
        ],
        emailId: [{ value: this.userDetail.emailId, disabled: true }, [Validators.required]],
        street1: [this.userDetail.street1 ? this.userDetail.street1 : ''],
        street2: [this.userDetail.street2 ? this.userDetail.street2 : ''],
        city: [this.userDetail.city ? this.userDetail.city : ''],
        country: [
          { value: this.userDetail.country ? this.userDetail.country : '' },
          [Validators.required]
        ],
        countryCode: [this.userDetail.countryCode ? this.userDetail.countryCode : ''],
        postcode: [this.userDetail.postcode ? this.userDetail.postcode : ''],
        userRole: [
          {
            value: this.userDetail.userRole[0].name,
            disabled: true
          },
          [Validators.required]
        ],
        language: [this.userDetail.language ? this.userDetail.language : '', [Validators.required]],
        timeZone: [this.userDetail.timeZone ? this.userDetail.timeZone : this.defaultTimeZone],
        communicationEmail: [
          this.userDetail.communicationEmail ? this.userDetail.communicationEmail : false
        ],
        communicationSms: [
          this.userDetail.communicationSms ? this.userDetail.communicationSms : false
        ],
        communicationPush: [
          this.userDetail.communicationPush ? this.userDetail.communicationPush : false
        ]
      });
      this.base64Image = this.userDetail.profilePicture;
    }

    this.subscriptions.push(
      this.commonConfigFacade.countries$.subscribe(
        (response: any) => {
          this.commonConfigFacade.getOrgDetails$.subscribe((org) => {
            if (org.nodeDetail && org.nodeDetail.country) {
              const newcountries = response.filter(
                (res) => res && res.name !== org.nodeDetail.country.name
              );
              this.countryList = [org.nodeDetail.country].concat(newcountries);
            } else {
              this.countryList = response;
            }
            if (this.userDetail && this.userDetail.country) {
              const selectedCountry = this.countryList.find(
                (country) => country.name === this.userDetail.country.name
              );
              this.userProfileForm.get('country').setValue(selectedCountry);
              const selectedCountryCode = this.countryList.find(
                (country) => country.country_code === this.userDetail.countryCode
              );
              console.log('selectedCountryCode :::', selectedCountryCode);
              this.selectedCountryCode = selectedCountryCode.country_code;
              this.userProfileForm.get('countryCode').setValue(selectedCountryCode.country_code);
            }
          });
        },
        (error) => throwError(error)
      )
    );

    this.subscriptions.push(
      this.commonConfigFacade.languages$.subscribe((data) => {
        this.languageList = data;
        if (this.userDetail && this.userDetail.language) {
          const selectedLanguage = this.languageList.find(
            (language) => language.name === this.userDetail.language.name
          );
          this.userProfileForm.get('language').setValue(selectedLanguage);
        }
      })
    );

    this.commonConfigFacade.timezones$.subscribe((data) => {
      this.timeZones = data;
      const selectedTimeZone = this.timeZones.find(
        (timezone) =>
          `(${timezone.abbreviation}${timezone.offset}) ${timezone.name}` ===
          this.userDetail.timeZone
      );
      this.userProfileForm
        .get('timeZone')
        .setValue(selectedTimeZone ? selectedTimeZone : this.defaultTimeZone);
    });
  }

  async onSubmit() {
    if (this.userProfileForm.invalid) {
      for (const i in this.userProfileForm.controls) {
        if (this.userProfileForm.controls[i]) {
          this.userProfileForm.controls[i].markAsTouched();
          this.userProfileForm.controls[i].markAsDirty();
        }
      }
      return;
    }
    const userProfile = this.userProfileForm.getRawValue() as IUser;

    userProfile.manualPassword = this.userDetail.manualPassword;
    userProfile.status = this.userDetail.status;
    userProfile.id = this.userDetail.id;
    userProfile.updated_by = this.userDetail.userName;
    userProfile.timeZone = `(${userProfile.timeZone['abbreviation']}${userProfile.timeZone['offset']}) ${userProfile.timeZone['name']}`;
    userProfile.userRole = this.userDetail.userRole;
    userProfile.profilePicture = '';
    if (this.base64Image) {
      let photoUploadRequest: object;
      const iend = this.base64Image.indexOf(';');
      if (iend > -1) {
        var timestamp = Number(new Date());
        photoUploadRequest = {
          photo: this.base64Image.replace(/^data:image\/(png|jpg|jpeg);base64,/, ''),
          fileName:
            'profile/' + timestamp + userProfile.id + '.' + this.base64Image.substring(11, iend)
        };
        const photoS3Url: IProfilePhotoUpdate = await this.profileService.saveProfilePhotoToS3(
          photoUploadRequest
        );
        userProfile.profilePicture = photoS3Url.url;
      }
    } else if (this.userDetail.profilePicture !== null) {
      userProfile.profilePicture = this.userDetail.profilePicture;
    }
    userProfile.pin = this.userDetail.pin;
    userProfile.domainName = this.userDetail.orgConfig.domainName;
    Object.keys(userProfile).forEach(
      (key) => (userProfile[key] == null || userProfile[key] === '') && delete userProfile[key]
    );
    this.updatedUser.emit(userProfile);
  }

  setImageBase64(event) {
    this.base64Image = event;
    this.isAnyValueUpdated = true;
  }

  getCountryCodeValue() {
    this.selectedCountryCode = this.userProfileForm.get('countryCode').value;
  }

  cancel() {
    if (this.isChangeUserFormValue) {
      const message = this.translate.instant('profile.confirmation_message');
      this.confirmDialogService.showConfirmDialog(message).subscribe((result) => {
        if (result === 'yes') {
          this.router.navigate([`/${USER_DASHBOARD_BASE_PATH}`]);
        }
      });
    } else {
      this.router.navigate([`/${USER_DASHBOARD_BASE_PATH}`]);
    }
  }

  subscribeFormValueChanges() {
    this.userProfileForm.valueChanges.subscribe((value) => {
      this.isChangeUserFormValue = true;
      Object.keys(value).forEach(
        (key) => (value[key] == null || value[key] === '') && delete value[key]
      );
      const diff = this.checkDifference(value, this.userDetail);
      if (Object.keys(diff).length) {
        this.isAnyValueUpdated = true;
      } else {
        this.isAnyValueUpdated = false;
      }
    });
  }

  private checkDifference(object, base) {
    return _.transform(object, (result, value, key) => {
      if (!_.isEqual(value, base[key])) {
        result[key] =
          _.isObject(value) && _.isObject(base[key])
            ? this.checkDifference(value, base[key])
            : value;
      }
    });
  }
}
